import { Redirect, Slot, useLocalSearchParams } from 'expo-router'
import type { ReactNode } from 'react'
import { ActivityIndicator } from 'react-native'

import type { TEventOriginParam } from '@/src/general/constants/routes'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { useRoutes } from '@/src/general/utils/routes'

import { EVENT_ORIGIN } from '../../models/community.constants'
import { useCommunityQuery } from '../../services/useCommunityQuery'
import { usePublicationsOfCommunityQuery } from '../../services/usePublicationsOfCommunityQuery'
import { isVirtusCommunity } from '../../utils/isVirtusCommunity'
import { useTodayPublication } from '../hooks/useTodayPublication'

/**
 * Guards the pages dedicated to Virtus journeys: other communities, or a Virtus journey
 * with nothing to pray today, are redirected to the community page.
 * Queries are cached, so the guarded pages get the community and its publications without refetching.
 */
export const VirtusOnlyLayout = (): ReactNode => {
  const { id: idParam, origin = EVENT_ORIGIN.UNDEFINED } = useLocalSearchParams<
    { id: string } & Partial<TEventOriginParam>
  >()
  const { routes } = useRoutes()

  const id = idParam ? parseInt(idParam, 10) : NaN
  const isValidId = id > 0

  const communityQuery = useCommunityQuery(isValidId ? id : undefined)
  const community = communityQuery.data
  const isVirtus = !!community && isVirtusCommunity(community)

  const publications = usePublicationsOfCommunityQuery({
    id,
    type: community?.lifetime,
    enabled: isVirtus,
  })
  const { todayPublication, isSearching: isSearchingTodayPublication } = useTodayPublication(publications)

  if (!isValidId) return <Redirect href={routes.communities()} />

  if (communityQuery.isError)
    return (
      <ErrorScreen
        error={communityQuery.error}
        defaultErrorKey="community.load.error"
        fallbackRoute={routes.communities()}
      />
    )

  if (!community) return <ActivityIndicator size="large" className="mt-10 self-center" />

  const communityRoute = routes.community({ id, slug: community.slug, origin })

  if (!isVirtus) return <Redirect href={communityRoute} />

  if (isSearchingTodayPublication) return <ActivityIndicator size="large" className="mt-10 self-center" />

  if (!todayPublication) return <Redirect href={communityRoute} />

  return <Slot />
}
